/**
 * Everything the site says, in one place.
 *
 * Taken from ClientMa_Platform_Technical_Specification_v1.0 rather than written fresh, so the
 * page and the product describe the same thing. Something not built yet says planned — a
 * marketing site that promises what does not exist is found out on the first sales call.
 *
 * The rule cuts the other way too, and did: SRM said planned here long after the platform's own
 * application catalogue had marked it available. A page that undersells a shipped product costs
 * exactly as much as one that oversells a missing one, and is harder to notice.
 *
 * Inventory moved to available the same way, and only once it could be bought. It had shipped
 * and had no plans in the catalogue, so calling it available would have sent somebody to a
 * pricing page that did not list it — which is the oversell this file exists to prevent, wearing
 * a different hat.
 */

export type Availability = 'available' | 'planned' | 'addon';

export interface Product {
  key: string;
  name: string;
  summary: string;
  availability: Availability;
  /** Commercial treatment, from the specification's release table. */
  commercial: string;
  capabilities: string[];
}


/**
 * The label a product's card carries, or null where it needs none.
 *
 * Only what a visitor cannot simply subscribe to is marked. "Available now" on most of the
 * cards was noise — a label three of six share carries no information, and the eye that learns
 * to skip it skips the one that says Planned as well.
 */
export function availabilityLabel(availability: Availability): string | null {
  switch (availability) {
    case 'planned':
      return 'Planned';
    case 'addon':
      return 'Add-on';
    default:
      return null;
  }
}

/** The pill's modifier, for the two labels that are still shown. */
export function availabilityTone(availability: Availability): string {
  return availability === 'addon' ? 'addon' : 'planned';
}

/** §1 Executive summary, §9–§12, §16. */
export const products: Product[] = [
  {
    key: 'crm',
    name: 'ClientMa CRM',
    summary:
      'The sales cycle end to end — from a lead nobody has called yet to an invoice that has been paid.',
    availability: 'available',
    commercial: 'Paid application subscription',
    capabilities: [
      'Leads, accounts, contacts, deals, pipeline and forecasts',
      'Quotations, sales orders and invoices with approval routing',
      'Campaigns and marketing email with consent handling',
      'Calendar, tasks, meetings and calls against any record',
      'Products, pricing groups and price books',
      'Tickets and a knowledge base for support',
    ],
  },
  {
    key: 'srm',
    name: 'ClientMa SRM',
    summary:
      'The buying side end to end — from a supplier nobody has approved yet to a delivery that '
      + 'arrived late and the corrective action it triggered.',
    availability: 'available',
    commercial: 'Separate subscription. SRM stands alone — it does not require CRM.',

    /*
     * Every line here is something the shipped module does. The two that were on this list
     * before — spend analytics and savings reporting — have been taken off, because neither
     * exists. Under a "Planned" pill they read as a roadmap and were fair. Under "Available now"
     * they would be a claim about a product a buyer can subscribe to today, and the first
     * demonstration would find them out.
     */
    capabilities: [
      'Supplier onboarding, qualification, approval and a watchlist',
      'A supplier portal where suppliers answer for themselves — questionnaires, documents, bids',
      'Sourcing events with sealed bids, clarifications and side-by-side comparison',
      'Purchase requests, an approval matrix and orders',
      'Contracts, renewals, obligations and expiry tracking',
      'Scorecards, business reviews, a risk register, quality events and CAPA',
    ],
  },
  {
    key: 'inventory',
    name: 'ClientMa Inventory',
    summary:
      'What you hold, where it is, what it cost and what is on its way — on a ledger that can '
      + 'prove its own balances.',
    availability: 'available',
    commercial: 'Separate subscription. Inventory stands alone — it needs neither CRM nor SRM.',

    /*
     * Every line is something the shipped module does, and the first one is the claim the rest
     * rests on: balances are a cache of the movement ledger and the reconciliation re-sums it on
     * demand. A stock system that cannot show its working is one nobody believes the second time
     * a count disagrees.
     */
    capabilities: [
      'A movement ledger balances are derived from, and a reconciliation that re-sums it',
      'Item master, sites, locations, lots and serials with full traceability',
      'Receipts, issues, transfers, adjustments and reversals with reasons',
      'Reservations and availability to promise, read by the rest of the suite',
      'FIFO and average costing, landed cost, valuation and period close',
      'Cycle and physical counting, blind counts, recounts and variance approval',
      'Replenishment planning with ABC/XYZ classification and demand forecasting',
      'Packing lists in and delivery orders out — from CSV, Excel, PDF or an API',
    ],
  },
  {
    key: 'hr',
    name: 'ClientMa HR Suite',
    summary:
      'Independently subscribable modules. Buy HR Core alone, add Leave or Attendance, or take the bundle.',
    availability: 'planned',
    commercial: 'Individual subscriptions or HR bundle',
    capabilities: [
      'HR Core — employees, positions, contracts, documents and self-service',
      'Leave — types, balances, accruals, approvals and holidays',
      'Attendance & Time — shifts, rosters, timesheets and overtime',
      'Recruitment, Performance, Learning, Expenses and Payroll',
    ],
  },
  {
    key: 'ai',
    name: 'AI assistant',
    summary:
      'A contextual assistant that only ever sees what the signed-in person is already allowed to see.',
    availability: 'addon',
    commercial: 'Prepaid ClientMa credits, or your own AI key',
    capabilities: [
      'A separate assistant per application — sales, suppliers and stock each answer for themselves',
      'Every answer cites the records it was built from, so a figure can be checked rather than believed',
      'Grounded in what the person asking may already open, and nothing else',
      'ClientMa’s own model on prepaid credits, or your own OpenAI or Anthropic account',
      'Advisory by default: it can prepare an action, never perform one',
      'Off until an administrator switches it on and sets the budget',
    ],
  },
  {
    key: 'reports',
    name: 'Reports & API',
    summary: 'A report builder over authorised fields, and a documented API with webhooks.',
    availability: 'addon',
    commercial: 'Subscription, feature upgrade or package',
    capabilities: [
      'Report builder with joins, grouping, formulas and charts',
      'Saved views, scheduled delivery and export',
      'REST API with per-tenant credentials and rate limits',
      'Outbound webhooks with retry and delivery history',
    ],
  },
];

/** §1.1 Product principles. */
export const principles = [
  {
    title: 'One account, one sign-in',
    body: 'Single registration and single sign-on across every application a tenant subscribes to.',
  },
  {
    title: 'Strong tenant isolation',
    body: 'Every business query is constrained by tenant and by the companies a user is authorised to see.',
  },
  {
    title: 'Multi-company by design',
    body: 'Companies, branches, departments, teams and roles inside one tenant, not one tenant per company.',
  },
  {
    title: 'Configuration first',
    body: 'Fields, forms, layouts, workflows, templates, terminology, dashboards and permissions are configurable.',
  },
  {
    title: 'Country aware',
    body: 'Currency, time zone, language, date format, tax, holidays and statutory options follow the country.',
  },
  {
    title: 'Responsive web first',
    body: 'Desktop, tablet and mobile, with tables that become cards and forms that stack sensibly.',
  },
];

/** §4.1 Solutions. */
export const solutions = [
  {
    name: 'Distribution & Trading',
    body: 'Quote from a price book, convert to an order, release it in stages, and invoice what actually shipped — with credit checked before anything is committed.',
    points: [
      'Price books by customer segment and country',
      'Partial conversion with line-level remainders',
      'Credit limits, holds and prepayment',
    ],
  },
  {
    name: 'Industrial Sales',
    body: 'Long cycles, technical quotations and service after the sale. Contracts, planned visits and installed assets sit alongside the deal that created them.',
    points: [
      'Quotation revisions and approval routing',
      'Service contracts, planned maintenance and assets',
      'Forecasts by team, territory and department',
    ],
  },
  {
    name: 'Wholesale & Warehousing',
    body:
      'Stock that reconciles, a promise you can keep, and paperwork that arrives as somebody '
      + 'else’s PDF and still ends up on the right line. What is on order counts towards what '
      + 'you can sell, and what is reserved does not.',
    points: [
      'A movement ledger the balances are proved against',
      'Lots, serials, expiry and recall traceability',
      'Packing lists and delivery orders read from CSV, Excel or PDF',
    ],
  },
  {
    name: 'Professional Services',
    body: 'Pipeline, proposals and retainers, with the activity history that tells you who last spoke to a client and what was agreed.',
    points: [
      'Deals, proposals and recurring billing',
      'Calendar, tasks and meeting notes on every record',
      'Knowledge base and ticketing for client support',
    ],
  },
];

/** §13, §14, §15, §8 — what every application gets from the platform. */
export const platformCapabilities = [
  {
    title: 'Identity & access',
    body: 'Roles, profiles, field-level security, record sharing by company, department, hierarchy and team.',
  },
  {
    title: 'Organisation model',
    body: 'Tenant, companies, branches, departments, teams and users — with one default context per person.',
  },
  {
    title: 'Billing & entitlements',
    body: 'Subscriptions, seats, plan features, usage meters, trials, proration, renewals and dunning.',
  },
  {
    title: 'Email & notifications',
    body: 'Templates with tenant overrides, provider defaults, consent and suppression handling.',
  },
  {
    title: 'API & webhooks',
    body: 'Documented REST API, per-tenant credentials, rate limits, and outbound webhooks with retry.',
  },
  {
    title: 'Audit & data protection',
    body: 'Change history, export, retention and recoverable data through the whole subscription lifecycle.',
  },
];

/** §6.3 Lifecycle states — used on the pricing page to be plain about what happens if you stop paying. */
export const lifecycle = [
  'Trialing',
  'Active',
  'Past due',
  'Grace period',
  'Suspended',
  'Cancelled',
  'Expired',
];

/** §4.1 Resources. */
export const resources = [
  {
    title: 'Help centre',
    body: 'Task-based guidance for administrators and everyday users of each application.',
  },
  {
    title: 'Knowledge base',
    body: 'Longer articles on configuration, data import, security and country setup.',
  },
  {
    title: 'Release notes',
    body: 'What changed, when it shipped, and what an administrator may need to do about it.',
  },
  {
    title: 'API reference',
    body: 'Endpoints, authentication, object and field tables, webhooks and environment separation — generated from your own configuration, inside the workspace.',
  },
  {
    title: 'Security',
    body: 'Tenant isolation, encryption, access control, audit and data residency.',
  },
  {
    title: 'Status',
    body: 'Current availability of the platform and each application, with incident history.',
  },
];

export const company = {
  provider: 'SJS Tech',
  product: 'ClientMa',
  tagline: 'Business applications that grow with the organisation using them.',
  about:
    'SJS Tech builds ClientMa: a multi-tenant, subscription-based business application platform. '
    + 'Three applications are available today — CRM for the selling side, SRM for the buying side '
    + 'and Inventory for what sits between them — each subscribable on its own, and all sharing '
    + 'identity, security, billing, notifications, files, APIs and audit history. Human resources '
    + 'follows.',
  regions: 'India, Singapore and Malaysia, with country-aware currency, tax and statutory options.',
  stack: '.NET, React and Microsoft SQL Server.',
};
